import React, { useState } from 'react';
import { X, AlertTriangle, Loader2, Trash2, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import { adminDataAPI } from '../services/api';

// Major data modules (Insights omitted — derived data, recalculates automatically).
const MODULES = [
  { key: 'accounts', label: 'Accounts', note: 'Deletes all account records' },
  { key: 'tasks', label: 'Tasks', note: 'Deletes all task records' },
  { key: 'properties', label: 'Properties', note: 'Deletes all property records' },
  { key: 'hotel_lifecycle', label: 'Hotel Lifecycle', note: 'Deletes lifecycle records & activities' },
  { key: 'notifications', label: 'Notifications', note: 'Deletes all notifications' },
  { key: 'master_forms', label: 'Master Forms', note: 'Deletes onboarding/master form tasks' },
  { key: 'commercials', label: 'Commercials', note: 'Clears commercial data from accounts' },
  { key: 'qc_data', label: 'QC Data', note: 'Clears QC workflow data only' },
  { key: 'cx_listings', label: 'CX Listings', note: 'Clears CX/listing workflow data only' },
  { key: 'documents', label: 'Documents', note: 'Clears document checklist data only' },
  { key: 'bank_details', label: 'Bank Details', note: 'Clears banking information only' },
  { key: 'ota_information', label: 'OTA Information', note: 'Clears OTA information only' },
  { key: 'user_activities', label: 'User Activities', note: 'Deletes activity/audit/timeline logs' },
];

const DeleteDataModal = ({ isOpen, onClose }) => {
  const [selected, setSelected] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [results, setResults] = useState(null);

  if (!isOpen) return null;

  const reset = () => { setSelected([]); setConfirmText(''); setResults(null); };
  const close = () => { reset(); onClose(); };

  const toggle = (key) => setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const canDelete = selected.length > 0 && confirmText === 'DELETE' && !deleting;

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      const res = await adminDataAPI.deleteData(selected, 'DELETE');
      setResults(res.data.results || {});
      toast.success('Selected data deleted permanently');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deletion failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="delete-data-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-red-50">
          <h2 className="text-lg font-bold text-red-700 flex items-center gap-2">
            <Trash2 size={20} /> Delete Data
          </h2>
          <button onClick={close} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full" data-testid="delete-data-close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!results ? (
            <>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="text-red-500 flex-shrink-0" size={20} />
                <p className="text-sm text-red-700">
                  <strong>Warning:</strong> This action permanently deletes the selected data across the entire system and <strong>cannot be undone</strong>. Unselected modules are not affected.
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Select modules to delete:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {MODULES.map((m) => {
                    const on = selected.includes(m.key);
                    return (
                      <button key={m.key} onClick={() => toggle(m.key)} data-testid={`delete-module-${m.key}`}
                        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${on ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        {on ? <CheckSquare size={18} className="text-red-600 mt-0.5 flex-shrink-0" /> : <Square size={18} className="text-gray-300 mt-0.5 flex-shrink-0" />}
                        <span>
                          <span className={`block text-sm font-medium ${on ? 'text-red-700' : 'text-gray-800'}`}>{m.label}</span>
                          <span className="block text-xs text-gray-400">{m.note}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Type <span className="font-bold text-red-600">DELETE</span> to confirm
                </label>
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  data-testid="delete-confirm-input"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-300 outline-none"
                />
              </div>
            </>
          ) : (
            <div className="space-y-3" data-testid="delete-data-results">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700">
                Deletion complete. Summary of affected records:
              </div>
              <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
                {Object.entries(results).map(([mod, r]) => (
                  <div key={mod} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="font-medium text-gray-700">{MODULES.find((m) => m.key === mod)?.label || mod}</span>
                    <span className="text-gray-500">{r.error ? `Error: ${r.error}` : `${r.affected} record(s)`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between">
          <button onClick={close} className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium">
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button
              onClick={handleDelete}
              disabled={!canDelete}
              data-testid="delete-data-confirm-btn"
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
              Delete Selected Data
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteDataModal;
